import * as React from 'react'
import { httpGet, httpGetRoute, httpPost, httpRetryable } from "../utils/http.service"
import { I18nService } from "../utils/i18n.service"
import { CastingBase } from "../json-schemas/base/casting"
import { OfferedServiceBase } from "../json-schemas/base/offered_service"
import { OfferedServiceUpdateResponse } from "../json-schemas/serializers/offered-service-update-response"
import { TaskBase } from "../json-schemas/base/task"
import { ItemTaskBase } from "../json-schemas/base/item_task"
import { SupplierBase } from "../json-schemas/base/supplier"
import { BackofficeOfferedServicesIndexResponse } from '../json-schemas/serializers/backoffice-offered-services-index-response'
import { toUpperCaseFirst } from '../utils/lodash.service'
import InterventionPlaceSchema from '../../../../../../json_schemas/shared/intervention-place.json'

export type CastingMode = CastingBase['casting_mode']
export type InterventionPlace = OfferedServiceBase['intervention_places'][number]
export type Coverage = OfferedServiceBase['coverage']
export type Zones = OfferedServiceBase['zones']
export type MultiOpeningHours = OfferedServiceBase['multi_opening_hours']
export type WorkingDay = OfferedServiceBase['working_days'][number]
export type OfferedServiceType = OfferedServiceBase['offered_service_type']
export type ProductQuantityRemainingWarningRecipients = OfferedServiceBase['product_quantity_remaining_warning_recipients'][number]
export type AllowedCustomerKind = OfferedServiceBase['allowed_customer_kinds'][number]
export type EmployeePaymentFormula = 'fee_percentage' | 'fixed_price'

export const fullfilledMultiOpeningHours: MultiOpeningHours = {}
for (const workingDay of [1, 2, 3, 4, 5, 6, 7] as const) fullfilledMultiOpeningHours[workingDay] = [['00:00', '24:00']]

export const interventionPlaces = InterventionPlaceSchema.enum as InterventionPlace[]

interface RansackQuery {
  'q[s]'?: 'translations_name asc'
  'q[offered_service_type_eq]'?: OfferedServiceBase['offered_service_type']
  'q[rentable_unit_of_work_in]'?: OfferedServiceBase['rentable_unit_of_work'][]
  'q[id_in][]'?: number[]
}

type OfferedServiceStep = 'choose_service' | 'zones' | 'working_days' | 'price' | 'quantity' | 'duration'

const i18nScoped = new I18nService(require('./offered-services.service.yml'))

export function offeredServiceList (query: RansackQuery = {}): Promise<BackofficeOfferedServicesIndexResponse> {
  if (query["q[id_in][]"]?.length === 0) return Promise.resolve({ offered_services: [] })
  return httpGet(`/${httpGetRoute()}/offered_services`, query)
}

export async function offeredServiceUpdate (body: any, step: OfferedServiceStep): Promise<OfferedServiceUpdateResponse> {
  const url = `/${httpGetRoute()}/offered_services/${body.id}/${step}`
  const key = `${httpGetRoute()}_offered_service_${step}`
  const payload = {
    [key]: body,
    report_subscription_state_instead_of_sending_mails: true,
    report_prescriber_discount_percentage_allowed_instead_of_sending_mails: true
  }

  return httpRetryable('PATCH', url, payload, key)
}

export function offeredServiceCreate (body: any, step = 'choose_service') {
  const url = `/${httpGetRoute()}/offered_services`
  const key = `${httpGetRoute()}_offered_service_${step}`
  return httpPost(url, { [key]: body })
}

export function offeredServiceGetParamMetierValue (paramMetier: OfferedServiceBase['param_metier'], metierName: string, propertyName: string) {
  return paramMetier?.[metierName]?.params?.[propertyName]
}

export function offeredServiceSetParamMetierValue (paramMetier: OfferedServiceBase['param_metier'], metierName: string, propertyName: string, value: string | string[]) {
  paramMetier = JSON.parse(JSON.stringify(paramMetier || {}))
  paramMetier[metierName] ||= { params: {} }
  paramMetier[metierName].params[propertyName] = value
  return paramMetier
}

export function offeredServiceGetDefaultParamsForFirstEventDurationStrategy (type: OfferedServiceBase['first_event_duration_strategy']['type']): OfferedServiceBase['first_event_duration_strategy'] {
  switch (type) {
    case 'none': return { type: 'none' }
    case 'calculator': return { type: 'calculator' }
    case 'fixed_minimum': return { type: 'fixed_minimum', value: 0 }
    case 'coef_minimum': return { type: 'coef_minimum', value: 0 }
  }
}

export type OfferedServiceTask = Pick<TaskBase, 'id' | 'name' | 'list_position'>
export type OfferedServiceItemTask = Pick<ItemTaskBase, 'id' | 'name' | 'task_id' | 'list_position'>

export function offeredServiceIsItemTask<T extends OfferedServiceTask, U extends OfferedServiceItemTask> (taskOrItemTask: T | U): taskOrItemTask is U {
  return 'task_id' in taskOrItemTask
}

export function offeredServiceIsTask<T extends OfferedServiceTask, U extends OfferedServiceItemTask> (taskOrItemTask: T | U): taskOrItemTask is T {
  return !('task_id' in taskOrItemTask)
}

export function offeredServiceFlattenedTaskItemTaskTree<T extends OfferedServiceTask, U extends OfferedServiceItemTask> (
  tasks: T[], 
  itemTasks: U[]
) {
  const tree: (T | U)[] = []

  const firstDepth = (tasks as typeof tree).concat(itemTasks.filter(_ => !_.task_id))
    .sort((a, b) => a.list_position - b.list_position || a.name.localeCompare(b.name))

  for (const item of firstDepth) {
    tree.push(item)
    if (!offeredServiceIsItemTask(item)) {
      tree.push(
        ...itemTasks
          .filter(itemTask => itemTask.task_id === item.id)
          .sort((a, b) => a.list_position - b.list_position || a.name.localeCompare(b.name))
      )
    }
  }

  return tree
}

export function offeredServiceI18nInterventionPlace (interventionPlace: InterventionPlace, { capitalize = true }: { capitalize?: boolean } = {}): string {
  const translation = window.currentUser?.type === 'Employee' && interventionPlace === 'employees_place'
    ? i18nScoped.t('employees_place_employee')
    : (!window.currentUser || window.currentUser.type === 'Customer') && interventionPlace === 'customers_place'
    ? i18nScoped.t('intervention_place.customers_place_customer')
    : i18nScoped.t(`intervention_place.${interventionPlace}`)

  return capitalize ? toUpperCaseFirst(translation) : translation
}

export function offeredServiceI18nProductQuantityRemainingWarningRecipients (recipient: ProductQuantityRemainingWarningRecipients) {
  return i18nScoped.t(`product_quantity_remaining_warning_recipient.${recipient}`)
}

export function offeredServieI18nAllowedCustomerKind (allowedCustomerKind: AllowedCustomerKind) {
  return i18nScoped.t(`allowed_customer_kind.${allowedCustomerKind}`)
}

export function offeredServiceI18nCastingPriceLabel (fieldName: 'unit_price_cents' | 'casting_one_shot_price_cents' | 'casting_recurring_price_cents' | 'casting_duration_price_cents', os: Pick<OfferedServiceBase, 'rentable_unit_of_work' | 'casting_custom_price'>) {
  if (fieldName === 'unit_price_cents') return i18nScoped.t('casting_price_labels.unit_price_cents')
  if (os.rentable_unit_of_work === 'day') return i18nScoped.t('casting_price_labels.' + fieldName + '_per_day')
  if (os.rentable_unit_of_work === 'month') return i18nScoped.t('casting_price_labels.' + fieldName + '_per_month')
  if (os.rentable_unit_of_work === 'event') return i18nScoped.t('casting_price_labels.' + fieldName + '_flat')
  return i18nScoped.t('casting_price_labels.' + fieldName)
}

export function offeredServiceI18nEmployeeFeeLabel (
  fieldName: 'employee_one_shot_fee_percentage' | 'employee_product_fee_percentage' | 'employee_recurring_fee_percentage',
  rentableUnitOfWork: OfferedServiceBase['rentable_unit_of_work']
) {
  return i18nScoped.t('employee_fees.' + fieldName + (rentableUnitOfWork === 'casting' ? '_casting_unit_of_work_quotation' : ''))
}

export function offeredServiceI18nCastingMode (castingMode: CastingMode) {
  return i18nScoped.t('casting_mode.' + castingMode)
}

export function offeredServiceI18nMarketplacePriceMode (marketplacePriceMode: OfferedServiceBase['marketplace_price_mode']) {
  return i18nScoped.t('marketplace_price_mode.' + marketplacePriceMode)
}

export function offeredServiceI18nMarketplacePriceModeMultiline (marketplacePriceMode: OfferedServiceBase['marketplace_price_mode']) {
  const parts = offeredServiceI18nMarketplacePriceMode(marketplacePriceMode).split('—') as string[]
  return React.createElement(React.Fragment, null, parts.map((part, index) => React.createElement("div", { key: index, children: part })))
}

export function offeredServiceI18nFirstEventDurationStrategyType (type: OfferedServiceBase['first_event_duration_strategy']['type']) {
  return i18nScoped.t('first_event_duration_strategy_type.' + type)
}

export function offeredServiceI18nDontWorkOfDayOff (dontWorkOnDayOff: boolean, supplier: Pick<SupplierBase, 'dont_work_on_day_off'>) {
  if (dontWorkOnDayOff === null) return i18nScoped.t(`dont_work_on_day_off.null_${supplier.dont_work_on_day_off ? 'true' : 'false'}`)
  return i18nScoped.t(`dont_work_on_day_off.${dontWorkOnDayOff}`)
}

export function offeredServiceI18nStep (step: string) {
  return i18nScoped.t(`steps.${step}`)
}

export function offeredServiceIsCalculatorEntryOnlyForFirstEvent (key: string) {
  return ['no_cleaning_last_30_days', 'no_cleaning_last_30_days_coef'].includes(key)
}

export function offeredServiceFormatZoneTitle (zone: Zones[0]) {
  const orderedKeys = ['route', 'locality', 'postal_code', 'zipcode', 'administrative_area_level_1', 'administrative_area_level_2', 'country'] as const
  const foundkey = orderedKeys.find(key => zone[key])
  
  if (foundkey !== 'zipcode' && foundkey !== 'postal_code' && (zone.zipcode || zone.postal_code)) {
    return `${zone[foundkey]}, ${zone.zipcode || zone.postal_code} (${i18nScoped.t('zone_title.' + foundkey)})`
  }

  return `${zone[foundkey]} (${i18nScoped.t('zone_title.' + foundkey)})`
}

export function offeredServiceI18nSpecificPricingMode (mode: OfferedServiceBase['specific_pricing_mode']) {
  return i18nScoped.t(`specific_pricing_mode.${mode}`)
}

export function offeredServiceI18nCoverage (coverage: Coverage) {
  return i18nScoped.t(`coverage.${coverage}`)
}

export function offeredServiceNameDeletedSuffix (offeredService: Pick<OfferedServiceBase, 'name' | 'deleted_at'>) {
  return offeredService.deleted_at ? `${offeredService.name} (${i18nScoped.t('deleted')})` : offeredService.name
}
